"use client";

import { useQuery } from "@apollo/client";
import { LiquidPill } from "components/ui/v2";
import { PageShell } from "components/ui/v2/layout/page-shell";
import { ProfilePageContentV2 } from "components/ui/v2/profile/ProfilePageContentV2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlRecognition, gqlUser } from "gql";
import type { IRecognitionsResponse } from "gql/Recognitions/queries";
import type { ICoinTransactionsHistoryByUser } from "gql/User/queries";
import { BriefcaseBusiness, Coins, HeartHandshake, Trophy, UserRound } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import * as React from "react";

type ProfileMenu = "profile" | "progress" | "recognitions" | "exchanges" | "resume";
type ProfileTab = "personalData" | "evaluation" | "arcana";
type ResumeTab = "experiences" | "certifications" | "skills" | "fields" | "project";

const PROFILE_MENUS: Array<{ value: ProfileMenu; labelKey: string; icon: React.ReactNode; variant: any }> = [
  { value: "profile", labelKey: "Menu.profile", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "progress", labelKey: "Menu.progress", icon: React.createElement(Trophy, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "recognitions", labelKey: "Menu.recognitions", icon: React.createElement(HeartHandshake, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "exchanges", labelKey: "Menu.exchanges", icon: React.createElement(Coins, { className: "h-3.5 w-3.5" }), variant: "slate" },
  { value: "resume", labelKey: "Menu.resume", icon: React.createElement(BriefcaseBusiness, { className: "h-3.5 w-3.5" }), variant: "slate" },
];

const PROFILE_TABS: Array<{ value: ProfileTab; labelKey: string; variant: any }> = [
  { value: "personalData", labelKey: "Tabs.personalData", variant: "indigo" },
  { value: "evaluation", labelKey: "Tabs.evaluation", variant: "sky" },
  { value: "arcana", labelKey: "Tabs.arcana", variant: "violet" },
];

const RESUME_TABS: Array<{ value: ResumeTab; labelKey: string; variant: any; icon: React.ReactNode }> = [
  { value: "experiences", labelKey: "Tabs.experiences", variant: "indigo", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }) },
  { value: "certifications", labelKey: "Tabs.certifications", variant: "emerald", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }) },
  { value: "skills", labelKey: "Tabs.skills", variant: "violet", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }) },
  { value: "fields", labelKey: "Tabs.fields", variant: "sky", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }) },
  { value: "project", labelKey: "Tabs.project", variant: "gold", icon: React.createElement(UserRound, { className: "h-3.5 w-3.5" }) },
];

function asString(value: unknown) {
  return typeof value === "string" ? value : "";
}

export function ProfilePageV2() {
  const { t } = useTranslation("profile");
  const { t: tc, lang } = useTranslation("common");
  const router = useRouter();
  const { vendor } = useVendor();
  const { user, updateUserData } = useUserStoreContext((store) => ({
    user: store.user,
    updateUserData: store.updateUserData,
  }));

  const menu = (asString(router.query.menu) || "profile") as ProfileMenu;
  const profileTab = (asString(router.query.tab) || "personalData") as ProfileTab | ResumeTab;
  const activeMenu: ProfileMenu = PROFILE_MENUS.some((item) => item.value === menu) ? menu : "profile";
  const activeProfileTab: ProfileTab = PROFILE_TABS.some((item) => item.value === profileTab) ? (profileTab as ProfileTab) : "personalData";
  const activeResumeTab: ResumeTab = RESUME_TABS.some((item) => item.value === profileTab) ? (profileTab as ResumeTab) : "experiences";

  const visibleMenus = React.useMemo(
    () => PROFILE_MENUS.filter((item) => (item.value !== "exchanges" || vendor?.enableCoins) && (item.value !== "recognitions" || vendor?.enableCorporateValue)),
    [vendor?.enableCoins, vendor?.enableCorporateValue]
  );

  const pushMenu = (nextMenu: ProfileMenu) => {
    const nextTab = nextMenu === "profile" ? "personalData" : nextMenu === "resume" ? "experiences" : undefined;
    router.push({ pathname: router.pathname, query: { menu: nextMenu, ...(nextTab ? { tab: nextTab } : {}) } });
  };

  const pushTab = (tab: ProfileTab | ResumeTab) => {
    router.push({ pathname: router.pathname, query: { menu: activeMenu, tab } });
  };

  const { data: recognitionData } = useQuery<IRecognitionsResponse>(gqlRecognition.queries.GET_RECOGNITIONS, {
    variables: { userId: user?.id },
    fetchPolicy: "network-only",
    skip: !user?.id,
  });
  const { data: coinData, loading: coinsLoading } = useQuery<ICoinTransactionsHistoryByUser>(gqlUser.queries.GET_USER_COINS_TRANSACTIONS_HISTORY, {
    fetchPolicy: "network-only",
    skip: !vendor?.enableCoins,
  });
  const { data: userLevelData, loading: userLevelLoading } = useQuery(gqlUser.queries.USER_LEVEL, { fetchPolicy: "network-only" });
  const { data: mySpaceData, loading: mySpaceLoading } = useQuery(gqlUser.queries.GET_MY_SPACE, { fetchPolicy: "network-only" });

  const recognitions = (recognitionData as any)?.getRecognitionCount ?? [];
  const exchanges = coinData?.coinTransactionHistoryByUser ?? [];
  const profileCompletion = React.useMemo(() => {
    const fields = [user?.name, user?.lastname, user?.email, user?.birthday, user?.shortBio, user?.linkedin || user?.github || user?.instagram, user?.profileImage];
    const done = fields.filter(Boolean).length;
    return Math.round((done / fields.length) * 100);
  }, [user]);

  if (!user) {
    return (
      <PageShell
        header={{
          title: t("Header.tittle"),
          description: t("Header.description"),
          particleIcon: UserRound,
        }}
        loading
        loadingTitle={tc("Cargando")}
        loadingDescription={tc("Espere por favor")}
      />
    );
  }

  return (
    <PageShell
      header={{
        leadingIcon: <UserRound className="h-10 w-10 shrink-0 text-indigo-600 dark:text-indigo-300" />,
        title: t("Header.tittle"),
        description: t("Header.description"),
        particleIcon: UserRound,
        badges: (
          <>
            <LiquidPill variant="indigo" appearance="tinted" size="sm" icon={<UserRound className="h-4 w-4" />} text={`${profileCompletion}% ${tc("perfil")}`} />
            <LiquidPill variant="indigo" appearance="tinted" size="sm" icon={<Coins className="h-4 w-4" />} text={`${user.coins ?? 0} ${tc("monedas")}`} />
            <LiquidPill variant="indigo" appearance="tinted" size="sm" icon={<Trophy className="h-4 w-4" />} text={`${(userLevelData as any)?.userLevel?.level ?? 0} ${tc("nivel")}`} />
          </>
        ),
      }}
    >
      <ProfilePageContentV2
        user={user}
        vendor={vendor}
        activeMenu={activeMenu}
        activeProfileTab={activeProfileTab}
        activeResumeTab={activeResumeTab}
        visibleMenus={visibleMenus}
        pushMenu={pushMenu}
        pushTab={pushTab}
        updateUserData={updateUserData}
        currentLang={lang}
        recognitions={recognitions}
        exchanges={exchanges}
        coinsLoading={coinsLoading}
        userLevelLoading={userLevelLoading}
        mySpaceLoading={mySpaceLoading}
        userLevel={(userLevelData as any)?.userLevel}
        mySpace={(mySpaceData as any)?.mySpace}
      />
    </PageShell>
  );
}
